import { confirmation, notify, RichTextValue } from '@/components';
import {
  useAcceptAnnouncementMutation,
  useGetPendingAnnouncementsQuery,
} from '@/redux/announcements';
import {
  useAcceptTermsAndConditionMutation,
  useUserContextSelector,
} from '@/redux';
import { useGetPendingTermsAndConditionsQuery } from '@/redux/termsAndConditions';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

export function RequiredConfirmationsGuard() {
  const { t } = useTranslation();

  const userId = useUserContextSelector((x) => x.id);

  const { data: pendingTerms, isFetching: isTermsFetching } =
    useGetPendingTermsAndConditionsQuery({});

  const { data: pendingAnnouncements, isFetching: isAnnouncementsFetching } =
    useGetPendingAnnouncementsQuery({});

  const [acceptTerms] = useAcceptTermsAndConditionMutation();
  const [acceptAnnouncement] = useAcceptAnnouncementMutation();

  const [acceptedAnnouncementIds, setAcceptedAnnouncementIds] = useState<
    string[]
  >([]);

  const [acceptedTermsVersion, setAcceptedTermsVersion] = useState<number>();

  const activeConfirmationIdRef = useRef<string>();
  const shownAnnouncementIdRef = useRef<string>();
  const shownTermsVersionRef = useRef<number>();

  const pendingTermsVersion = pendingTerms?.version;

  const nextAnnouncement = useMemo(() => {
    return pendingAnnouncements?.find(
      (announcement) => !acceptedAnnouncementIds.includes(announcement.id),
    );
  }, [pendingAnnouncements, acceptedAnnouncementIds]);

  useEffect(() => {
    if (activeConfirmationIdRef.current) {
      return;
    }

    const hasPendingTerms =
      !isTermsFetching &&
      pendingTerms &&
      pendingTermsVersion &&
      acceptedTermsVersion !== pendingTermsVersion;

    if (
      hasPendingTerms &&
      shownTermsVersionRef.current !== pendingTermsVersion
    ) {
      activeConfirmationIdRef.current = 'terms';
      shownTermsVersionRef.current = pendingTermsVersion;

      confirmation.show({
        title: t('termsAndConditions.accept.title'),
        confirm: t('termsAndConditions.accept.confirm'),
        hideCancelButton: true,
        maskClosable: false,
        body: (
          <RichTextValue
            ownerType="TermsAndConditions"
            value={pendingTerms.content}
            label={false}
            ownerId={pendingTerms.id}
            scrollable={false}
          />
        ),
        onConfirm: async () => {
          await acceptTerms({
            id: userId,
            termsId: pendingTerms.id,
          }).unwrap();

          notify.success.t('termsAndConditions.accept.saved');

          setAcceptedTermsVersion(pendingTermsVersion);
          shownTermsVersionRef.current = undefined;
          activeConfirmationIdRef.current = undefined;
        },
      });

      return;
    }

    if (
      isAnnouncementsFetching ||
      !nextAnnouncement ||
      shownAnnouncementIdRef.current === nextAnnouncement.id
    ) {
      return;
    }

    activeConfirmationIdRef.current = 'announcement';
    shownAnnouncementIdRef.current = nextAnnouncement.id;

    confirmation.show({
      title: nextAnnouncement.title,
      confirm: t('announcements.accept.confirm'),
      hideCancelButton: true,
      maskClosable: false,
      body: (
        <RichTextValue
          ownerType="Announcement"
          value={nextAnnouncement.body}
          label={false}
          ownerId={nextAnnouncement.id}
          scrollable={false}
        />
      ),
      onConfirm: async () => {
        await acceptAnnouncement({ id: nextAnnouncement.id }).unwrap();

        setAcceptedAnnouncementIds((value) => [...value, nextAnnouncement.id]);

        shownAnnouncementIdRef.current = undefined;
        activeConfirmationIdRef.current = undefined;

        notify.success.t('announcements.accept.saved');
      },
    });
  }, [
    acceptAnnouncement,
    acceptedTermsVersion,
    acceptTerms,
    isAnnouncementsFetching,
    isTermsFetching,
    nextAnnouncement,
    pendingTerms,
    pendingTermsVersion,
    t,
    userId,
  ]);

  return <Outlet />;
}
